import {
  FormattedString,
  ItemStyle,
  OverviewContentSection,
  OverviewGettingStartedSection,
  OverviewInfoBlockButton,
  OverviewInfoBlockSection,
  OverviewInfoBlockStatus,
  OverviewTemplate,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { Spinner } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import * as Messages from "../../codegen/Messages";
import {
  CloudLinkAdminInstructionsPanel,
  ListSubscriptionsInstructionsPanel,
} from "../../components/InstructionsPanel/InstructionsPanel";
import { PromoAlertModal } from "../../components/PromoAlertModal/PromoAlertModal";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  gettingStartedADB,
  gettingStartedExadata,
  gettingStartedMySQL,
  gettingStartedProvisioning,
  gettingStartedVMDB,
} from "../../constants/docConstants";
import {
  ADB_LIST_HUB_ROUTE,
  APEX_LIST_ROUTE,
  BILLING_ROUTE,
  CLOUDLINK_LIST_ROUTE,
  CREATE_HUB_ROUTE,
  DEPLOYMENT_LIST_ROUTE,
  DetailsPanelId,
  EXADATA_LIST_HUB_ROUTE,
  HOME_ROUTE,
  InfoPanelId,
  MCVCN_LIST_ROUTE,
  MYSQL_LIST_ROUTE,
  PageId,
  PageRegistrationConfig,
  SUPPORT_ROUTE,
  VMDB_LIST_ROUTE,
} from "../../constants/pluginConstants";
import { SvgIconIds } from "../../constants/uiConstants";
import { getTrialAccountTypeMessage } from "../../helpers/resourceHelper";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useAppAuthContext, UserType } from "../../hooks/useAppAuthContext";
import { useFeatures } from "../../hooks/useFeatures";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { getOCIConsoleUrl, getOciRegion } from "../../utils";

export enum ActionIds {
  UpgradeAccount = "upgrade-account",
}

const enum NewSidePanels {
  ODSA_ROLES = "ODSA_ROLES",
  SUBSCRIPTION_ROLE = "SUBSCRIPTION_ROLE",
}

export enum SectionsTestIds {
  GettingStarted = "getting-started",
  NewUserGettingStarted = "new-user-getting-started",
  ContentOCIServices = "oci-services",
  ContentNavigate = "navigate",
  Links = "overview-links",
}

export enum ContentOCIServicesItemsTestIds {
  CreateResource = "create-resource-item",
  AutonomousDatabases = "adb-item",
  ExaDatabase = "exadb-item",
  VmDatabase = "vmdb-item",
  MysqlDatabase = "mysql-item",
  Apex = "apex-item",
}

export enum ContentNavigateItemsTestIds {
  Deployments = "deployment-item",
  Mcvcn = "mcvcn-item",
  Billing = "bill-item",
  CloudLinks = "subscription-item",
  Support = "support-item",
}

export enum GettingStartedItemsTestIds {
  AssignRoles = "assign-roles",
  LinkSubscription = "link-subscription",
}

export enum NewUserGettingStartedItemsTestIds {
  AssignSubscriptionRole = "assign-subscription-role",
}

export enum InfoBlockButtonTestIds {
  UpgradeButton = "upgrade-button-info-block"
}

const registrationIds = PageRegistrationConfig[PageId.HOME].map(config => config.key);

export const HomePage = (): JSX.Element => {
  const filterState = React.useContext<FilterState>(FilterStateContext);

  React.useEffect(() => {
    document.title = Messages.common.home();
    filterState.setFilterStateValue({ filterValues: undefined, listId: undefined, defaultPills: undefined });
    filterState.setFilterTextValue(undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { enableMcvcn, enableMds, enableApex } = useFeatures();

  const { trackActionClick, trackLinkNavigate } = useAnalytics();

  const { roles, userType } = useAppAuthContext();
  const { preferredSubscription } = React.useContext<Settings>(SettingsContext);
  const { loading: subscriptionsLoading, subscriptions } = useSubscriptions();
  const preferredSubscriptionInfo = subscriptions?.find(sub => sub.id === preferredSubscription);
  const [sidePanelOpen, setSidePanelOpen] = React.useState<NewSidePanels | undefined>(undefined);
  const [isPromoAlertModalOpen, { setTrue: showPromoAlertModal, setFalse: hidePromoAlertModal }] = useBoolean(false);
  const noLinkedSubscriptions = userType === UserType.REGULAR_USER && subscriptions?.length === 0;

  const { navigateTo, navigateToSelf } = useNavigation(ConsoleContext);
  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    navigateToSelf(HOME_ROUTE, registrationIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let isPromoAccount = false;
  if (subscriptions) {
    for (let i = 0; i < subscriptions.length; i++) {
      if (subscriptions[i].ociTenancyPaymentModel === "FREE") {
        isPromoAccount = true;
      } else {
        isPromoAccount = false;
        break;
      }
    }
  }
  const onResourceCreate = (): void => {
    navigateTo(CREATE_HUB_ROUTE, PageId.CREATE_A_RESOURCE);
  };

  const onAutonomousDatabases = (): void => {
    navigateTo(
      `${ADB_LIST_HUB_ROUTE}/${PageRegistrationConfig[PageId.ADB_LIST_HUB][0].panelPath}`,
      PageRegistrationConfig[PageId.ADB_LIST_HUB][0].key,
    );
  };

  const onApex = (): void => {
    navigateTo(APEX_LIST_ROUTE, PageId.APEX_LIST);
  };

  const onExaDatabase = (): void => {
    if (isPromoAccount) {
      showPromoAlertModal();
    } else {
      navigateTo(
        `${EXADATA_LIST_HUB_ROUTE}/${PageRegistrationConfig[PageId.EXA_LIST_HUB][0].panelPath}`,
        PageRegistrationConfig[PageId.EXA_LIST_HUB][0].key,
      );
    }
  };

  const onVmDatabase = (): void => {
    navigateTo(VMDB_LIST_ROUTE, PageId.VMDB_LIST);
  };

  const onMysqlDatabase = (): void => {
    navigateTo(MYSQL_LIST_ROUTE, PageId.MYSQL_LIST);
  };

  const onMcvcn = (): void => {
    navigateTo(MCVCN_LIST_ROUTE, PageId.MCVCN_LIST);
  };

  const onDeployments = (): void => {
    navigateTo(DEPLOYMENT_LIST_ROUTE, PageId.DEPLOYMENT_LIST);
  };

  const onSupport = (): void => {
    navigateTo(SUPPORT_ROUTE, PageId.SUPPORT);
  };

  const onCloudLinks = (): void => {
    navigateTo(CLOUDLINK_LIST_ROUTE, PageId.CLOUDLINK_LIST);
  };

  const onODSARoles = (): void => {
    setSidePanelOpen(NewSidePanels.ODSA_ROLES);
  };

  const onSubscriptionRole = (): void => {
    setSidePanelOpen(NewSidePanels.SUBSCRIPTION_ROLE);
  };

  const onBilling = (): void => {
    navigateTo(BILLING_ROUTE, PageId.BILLING);
  };

  const gettingStartedSections: OverviewGettingStartedSection[] = [
    {
      testId: SectionsTestIds.GettingStarted,
      title: Messages.home.sections.gettingStarted(Messages.portalBrand.long()),
      items: [
        {
          image: <img src="/azure/odsaroles.png" alt="" />,
          title: Messages.home.gettingStarted.step1Title(Messages.portalBrand.short()),
          description: Messages.home.gettingStarted.step1Description(),
          buttonLabel: roles?.includes("cloudlink-administrator")
            || roles?.includes("odsa-multicloudlink-administrator")
            ? Messages.labels.completed()
            : Messages.actions.getStarted(),
          onClick: () => {
            trackLinkNavigate(InfoPanelId.INSTRUCTIONS_ADMIN_ROLES, PageId.HOME);
            onODSARoles();
          },
          testId: GettingStartedItemsTestIds.AssignRoles,
          disabled: roles?.includes("cloudlink-administrator")
            || roles?.includes("odsa-multicloudlink-administrator"),
        },
        {
          image: <img src="/azure/linksubscriptions.png" alt="" />,
          title: Messages.home.gettingStarted.step2Title(),
          description: Messages.home.gettingStarted.step2Description(),
          buttonLabel: Messages.actions.getStarted(),
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.CLOUDLINK_LIST }, PageId.HOME);
            onCloudLinks();
          },
          testId: GettingStartedItemsTestIds.LinkSubscription,
          disabled: false,
        },
      ],
    },
  ];

  const newUserGettingStarted: OverviewGettingStartedSection[] = [
    {
      testId: SectionsTestIds.NewUserGettingStarted,
      title: Messages.home.sections.gettingStarted(Messages.portalBrand.long()),
      items: [
        {
          image: <img src="/azure/odsaroles.png" alt="" />,
          title: Messages.home.gettingStartedNewUser.title(Messages.portalBrand.short()),
          description: (
            <>
              <FormattedString
                inputText={Messages.home.gettingStartedNewUser.description(Messages.portalBrand.short())}
              />
              <p />
              <FormattedString
                inputText={Messages.home.gettingStartedNewUser.importantNote(Messages.portalBrand.short())}
              />
            </>
          ),
          buttonLabel: Messages.actions.getStarted(),
          onClick: () => {
            trackLinkNavigate(InfoPanelId.INSTRUCTIONS_SUBSCRIPTION_ROLES, PageId.HOME);
            onSubscriptionRole();
          },
          testId: NewUserGettingStartedItemsTestIds.AssignSubscriptionRole,
        },
      ],
    },
  ];

  const contentSections: OverviewContentSection[] = [
    {
      testId: SectionsTestIds.ContentOCIServices,
      title: Messages.home.sections.oracleService(),
      itemStyle: ItemStyle.VERTICAL,
      items: [
        {
          iconName: SvgIconIds.addSvg,
          title: Messages.actions.createAResource(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.CREATE_A_RESOURCE }, PageId.HOME);
            onResourceCreate();
          },
          testId: ContentOCIServicesItemsTestIds.CreateResource,
        },
        {
          iconName: SvgIconIds.adbSvg,
          title: Messages.topServices.autonomousDatabases(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.ADB_LIST_HUB, panelId: DetailsPanelId.ADBS_DATABASES }, PageId.HOME);
            onAutonomousDatabases();
          },
          testId: ContentOCIServicesItemsTestIds.AutonomousDatabases,
        },
        {
          iconName: SvgIconIds.exaSvg,
          title: Messages.topServices.exadata(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.EXA_LIST_HUB, panelId: DetailsPanelId.DATABASES }, PageId.HOME);
            onExaDatabase();
          },
          testId: ContentOCIServicesItemsTestIds.ExaDatabase,
        },
        {
          iconName: SvgIconIds.baseDbSvg,
          title: Messages.topServices.vmDatabase(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.VMDB_LIST }, PageId.HOME);
            onVmDatabase();
          },
          testId: ContentOCIServicesItemsTestIds.VmDatabase,
        },
        ...(enableMds ? [{
          iconName: SvgIconIds.mysqlSvg,
          title: Messages.topServices.mysqlDatabase(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.MYSQL_LIST }, PageId.HOME);
            onMysqlDatabase();
          },
          testId: ContentOCIServicesItemsTestIds.MysqlDatabase,
        }] : []),
        ...(enableApex ? [{
          iconName: SvgIconIds.apexSvg,
          title: Messages.topServices.apex(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.APEX_LIST }, PageId.HOME);
            onApex();
          },
          testId: ContentOCIServicesItemsTestIds.Apex,
        }] : []),
      ],
    },
    {
      testId: SectionsTestIds.ContentNavigate,
      title: Messages.home.sections.navigate(),
      itemStyle: ItemStyle.HORIZONTAL,
      items: [
        {
          iconName: SvgIconIds.deploymentSvg,
          title: Messages.topServices.deployments(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.DEPLOYMENT_LIST }, PageId.HOME);
            onDeployments();
          },
          testId: ContentNavigateItemsTestIds.Deployments,
        },
        ...(enableMcvcn ? [{
          iconName: SvgIconIds.networkSvg,
          title: Messages.topServices.mcvcn(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.MCVCN_LIST }, PageId.HOME);
            onMcvcn();
          },
          testId: ContentNavigateItemsTestIds.Mcvcn,
        }] : []),
        {
          iconName: SvgIconIds.billingSvg,
          title: Messages.topServices.billing(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.BILLING }, PageId.HOME);
            onBilling();
          },
          testId: ContentNavigateItemsTestIds.Billing,
        },
        {
          iconName: SvgIconIds.subscriptionSvg,
          title: Messages.topServices.subscriptionManagement(),
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.CLOUDLINK_LIST }, PageId.HOME);
            onCloudLinks();
          },
          testId: ContentNavigateItemsTestIds.CloudLinks,
        },
        {
          iconName: SvgIconIds.supportSvg,
          title: Messages.topServices.support(),
          disabled: noLinkedSubscriptions,
          onClick: () => {
            trackLinkNavigate({ pageId: PageId.SUPPORT }, PageId.HOME);
            onSupport();
          },
          testId: ContentNavigateItemsTestIds.Support,
        },
      ],
    },
  ];
  const linkSections = [
    {
      testId: SectionsTestIds.Links,
      title: Messages.home.sections.usefulLinks(),
      items: [
        {
          text: Messages.home.links.gettingStartedADB(),
          url: gettingStartedADB,
        },
        {
          text: Messages.home.links.gettingStartedExadata(),
          url: gettingStartedExadata,
        },
        ...(enableMds ? [{
          text: Messages.home.links.gettingStartedMySQL(),
          url: gettingStartedMySQL,
        }] : []),
        {
          text: Messages.home.links.gettingStartedVMDB(),
          url: gettingStartedVMDB,
        },
        {
          text: Messages.home.links.gettingStartedProvisioning(Messages.portalBrand.short()),
          url: gettingStartedProvisioning,
        },
      ],
    },
  ];

  const onUpgrade = (): void => {
    const region = getOciRegion(preferredSubscriptionInfo?.primaryLocation || "");
    const ociConsoleUrl = getOCIConsoleUrl();
    const ociTenancyParam = preferredSubscriptionInfo?.ociTenancyName
      ? `&tenant=${preferredSubscriptionInfo?.ociTenancyName}` : "";
    const ociDomainParam = preferredSubscriptionInfo?.ociDomainName
      ? `&domain=${preferredSubscriptionInfo?.ociDomainName}` : "";
    const upgradeUrl = `${ociConsoleUrl}/invoices-and-orders/upgrade-and-payment`
      + `?region=${region}${ociTenancyParam}${ociDomainParam}`;
    window.open(upgradeUrl, "_blank");
  };

  const upgradeNowLink: OverviewInfoBlockButton = {
    testId: InfoBlockButtonTestIds.UpgradeButton,
    text: Messages.labels.upgradeNow(),
    onClick: () => {
      trackActionClick(ActionIds.UpgradeAccount, PageId.HOME);
      onUpgrade();
    },
  };

  const promoInfoBlock: OverviewInfoBlockSection = {
    testId: "promo-info-section",
    message: getTrialAccountTypeMessage(preferredSubscriptionInfo?.ociTenancyPaymentModel),
    messageType: OverviewInfoBlockStatus.INFO,
    actionButtons: [upgradeNowLink],
  };

  const noAccessToLinkedSubscriptionsInfoBlock: OverviewInfoBlockSection = {
    testId: "no-access-to-linked-subscriptions-info-section",
    message: <FormattedString
      inputText={Messages.home.banner.noAccessToLinkedSubscriptions(Messages.portalBrand.short())}
    />,
    messageType: OverviewInfoBlockStatus.ERROR,
    isMultiline: true,
  };

  return (
    <div data-test-id="overview">
      {subscriptionsLoading && <Spinner />}
      {!subscriptionsLoading && (
        <OverviewTemplate
          gettingStartedSections={userType === UserType.FIRST_TIME_USER
            ? gettingStartedSections
            : userType === UserType.NEW_USER
              ? newUserGettingStarted
              : undefined}
          contentSections={userType !== UserType.REGULAR_USER ? undefined : contentSections}
          linkSections={linkSections}
          infoBlock={noLinkedSubscriptions
            ? noAccessToLinkedSubscriptionsInfoBlock
            : isPromoAccount
              ? promoInfoBlock
              : undefined}
        />
      )}
      {sidePanelOpen === NewSidePanels.ODSA_ROLES && (
        <CloudLinkAdminInstructionsPanel
          onClose={() => setSidePanelOpen(undefined)}
        />
      )}
      {sidePanelOpen === NewSidePanels.SUBSCRIPTION_ROLE && (
        <ListSubscriptionsInstructionsPanel
          onClose={() => setSidePanelOpen(undefined)}
        />
      )}
      <PromoAlertModal
        isPromoAlertModalOpen={isPromoAlertModalOpen}
        hidePromoAlertModal={hidePromoAlertModal}
        onUpgrade={onUpgrade}
      />
    </div>
  );
};
